import { useCallback, useMemo } from 'react';
import { debounce } from 'lodash-es';
import { useDispatch } from '@betty/toolkit/state';
import { findAddress } from '@betty/toolkit/api';

export const useDebouncedAddressSearch = () => {
  const dispatch = useDispatch();
  const performAddressSearch = useCallback(
    async (query: string, container?: string) => {
      const result = await dispatch(findAddress({ query, container }));
      return result?.payload?.Items ?? [];
    },
    [dispatch],
  );

  const debouncedAddressSearch = useMemo(
    () =>
      debounce(
        (query: string, container: string, resolve: (result: any) => void) => {
          performAddressSearch(query, container).then(resolve);
        },
        250,
      ),
    [performAddressSearch],
  );

  const performDebouncedAddressSearch = useCallback(
    (query: string, container?: string) => {
      return new Promise((resolve) => {
        debouncedAddressSearch(query, container || '', resolve);
      });
    },
    [debouncedAddressSearch],
  );

  const cancelSearch = useCallback(() => {
    debouncedAddressSearch.cancel();
  }, [debouncedAddressSearch]);

  return {
    performAddressSearch,
    performDebouncedAddressSearch,
    cancelSearch,
  };
};

export default useDebouncedAddressSearch;
